import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MainContent from '../components/MainContent';
import ImpactStat from '../components/impact/ImpactStat';
import OutcomesCard from '../components/impact/OutcomesCard';
import LearnersCard from '../components/impact/LearnersCard';
import YearOnYearChart from '../components/impact/YearOnYearChart';
import ProjectsCard from '../components/impact/ProjectsCard';
import DataQualityPanel, {
  dataQualityIssues,
} from '../components/impact/DataQualityPanel';
import LevelTable, { StatusPill } from '../components/impact/LevelTable';
import DownloadMenu from '../components/analytics/DownloadMenu';
import DownloadButton from '../components/analytics/DownloadButton';
import {
  SUPPRESSED_HINT,
  cohortCountLabel,
  formatCount,
  formatDate,
  formatPercent,
  funnelGraduationRate,
  noProgressNames,
} from '../components/impact/format';
import { excludedNote, yearNotes } from '../components/impact/notes';
import useImpactReport from '../hooks/useImpactReport';
import ImpactPdfDialog from '../components/impact/pdf/ImpactPdfDialog';

const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';

// Query string for /impact/export at the current level. year and cohort_id
// can't be combined, so a cohort view only sends cohort_id.
const exportQuery = (view, { year, cohortId }) => {
  const params = new URLSearchParams();
  if (view !== 'metrics') params.set('view', view);
  if (cohortId) params.set('cohort_id', cohortId);
  else if (year) params.set('year', year);
  const query = params.toString();
  return `/impact/export${query ? `?${query}` : ''}`;
};

const exportName = (view, { year, cohortId }) =>
  `impact-${view}-${cohortId ? 'cohort' : year || 'all'}.csv`;

const downloadOptions = (scope, onPdf) => [
  {
    label: 'Branded PDF report',
    description: 'A designed report to share with partners',
    onSelect: onPdf,
  },
  {
    label: 'Report (CSV)',
    description: 'Headline figures for every level shown',
    path: exportQuery('metrics', scope),
    fallbackName: exportName('metrics', scope),
  },
  {
    label: 'Demographics (CSV)',
    description: 'Gender, region, country, age and education',
    path: exportQuery('demographics', scope),
    fallbackName: exportName('demographics', scope),
  },
  {
    label: 'Projects (CSV)',
    description: 'Graduate projects by sector, SDG and partner',
    path: exportQuery('projects', scope),
    fallbackName: exportName('projects', scope),
  },
];

function Card({ title, aside, children }) {
  return (
    <section className={`${CARD} p-5`}>
      {(title || aside) && (
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          {title && (
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          )}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

function Note({ children }) {
  return (
    <p className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-600">
      {children}
    </p>
  );
}

function LoadingBlock() {
  return (
    <div className="space-y-5 animate-pulse" aria-label="Loading">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((key) => (
          <div key={key} className={`h-28 ${CARD}`} />
        ))}
      </div>
      <div className={`h-40 ${CARD}`} />
      <div className={`h-96 ${CARD}`} />
    </div>
  );
}

function ErrorBlock({ status, onRetry, onBack, notFoundText }) {
  let message = "We couldn't load the impact report.";
  if (status === 403) {
    message = 'The impact report is only available to administrators.';
  } else if (status === 404 || status === 400) {
    message = notFoundText;
  } else if (status === 502) {
    message = 'The analytics service is unreachable right now.';
  }
  const canRetry = !status || status >= 500;

  return (
    <div className={`${CARD} p-8 text-center space-y-4`}>
      <ErrorOutlineIcon className="text-gray-400" fontSize="large" />
      <p className="text-sm text-gray-600">{message}</p>
      <div className="flex justify-center gap-2">
        {onBack && status !== 403 && (
          <button
            type="button"
            onClick={onBack}
            className="border border-gray-300 px-4 py-1.5 rounded-md text-sm text-gray-700 hover:border-claret-500 hover:text-claret-500"
          >
            Back to all years
          </button>
        )}
        {canRetry && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="bg-claret-500 hover:bg-claret-400 px-4 py-1.5 rounded-md text-white text-sm"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

// The four headline numbers. The graduation rate comes from the funnel
// (ended cohorts with progress data) so live cohorts don't pull it down.
function Headline({ level, definitions, isCohort }) {
  const funnel = level.funnel ?? {};
  const covered = funnel.cohorts ?? 0;
  const women = (level.breakdowns?.gender ?? []).find(
    (bucket) => bucket.value === 'female'
  );
  const stillRunning = isCohort
    ? level.status !== 'ended'
    : (level.cohorts?.ended ?? 0) === 0;

  let rateDisplay = formatPercent(funnelGraduationRate(level));
  let rateDetail = `Of enrolled, ${covered} ended cohort${
    covered === 1 ? '' : 's'
  } with progress data`;
  if (covered === 0) {
    rateDisplay = stillRunning ? 'In progress' : '–';
    rateDetail = stillRunning
      ? 'Shown once the cohort ends'
      : 'No progress data for these cohorts';
  } else if (isCohort) {
    rateDetail = 'Of enrolled';
  }

  let enrolledDetail = cohortCountLabel(level.cohorts);
  if (isCohort) {
    enrolledDetail =
      level.drop_off > 0
        ? `${formatCount(level.drop_off)} moved to Drop-off`
        : undefined;
  }

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
      <ImpactStat
        label="Learners enrolled"
        display={formatCount(level.enrolled)}
        suppressed={level.enrolled === null}
        detail={enrolledDetail}
        info={definitions.enrolled}
      />
      <ImpactStat
        label="Graduates"
        display={formatCount(level.graduated)}
        suppressed={level.graduated === null}
        accent="#8B173B"
        info={definitions.graduated}
        detail={`${formatCount(
          level.graduated_participation
        )} participation · ${formatCount(
          level.graduated_achievement
        )} achievement`}
      />
      <ImpactStat
        label="Graduation rate"
        display={rateDisplay}
        detail={rateDetail}
        info={definitions.funnel}
      />
      <ImpactStat
        label="Women"
        display={women ? formatPercent(women.percent) : '–'}
        suppressed={women?.enrolled === null}
        detail={
          women
            ? `${formatCount(women.enrolled)} learners · ${formatCount(
                women.graduated
              )} graduates`
            : undefined
        }
      />
    </div>
  );
}

function ProjectsDownload({ scope }) {
  return (
    <DownloadButton
      label="Download projects"
      path={exportQuery('projects', scope)}
      fallbackName={exportName('projects', scope)}
    />
  );
}

function AllYearsView({ summary, onSelectYear, withoutProgress }) {
  const years = summary.years ?? [];
  const allTime = summary.all_time ?? {};
  const definitions = summary.definitions ?? {};
  const notes = yearNotes(years);
  const excluded = excludedNote(allTime);

  return (
    <>
      <Headline level={allTime} definitions={definitions} />
      <div>
        <OutcomesCard funnel={allTime.funnel} definitions={definitions} />
        {excluded && <p className="mt-2 text-xs text-gray-500">{excluded}</p>}
      </div>

      {years.length > 0 && (
        <Card title="By cohort start year">
          <YearOnYearChart
            years={years}
            withoutProgress={withoutProgress}
            onSelectYear={onSelectYear}
          />
          <div className="mt-5">
            <LevelTable
              kind="year"
              rows={[...years].reverse()}
              withoutProgress={withoutProgress}
              onSelect={onSelectYear}
            />
          </div>
          {notes.length > 0 && <Note>{notes.join(' ')}</Note>}
        </Card>
      )}

      <LearnersCard
        title="Who our learners are"
        breakdowns={allTime.breakdowns}
        scopeNote="all cohorts"
      />

      <ProjectsCard
        projects={allTime.projects}
        action={<ProjectsDownload scope={{}} />}
      />

      <DataQualityPanel issues={dataQualityIssues(allTime, summary.notes)} />
    </>
  );
}

function YearView({
  year,
  onSelectCohort,
  onBack,
  definitions,
  withoutProgress,
}) {
  const { data, isLoading, isError, errorStatus, refetch } = useImpactReport(
    `/impact/years/${year}`
  );

  if (isLoading) return <LoadingBlock />;
  if (isError || !data?.year) {
    return (
      <ErrorBlock
        status={errorStatus}
        onRetry={refetch}
        onBack={onBack}
        notFoundText={`There are no cohorts to report for ${year}.`}
      />
    );
  }

  const level = data.year;
  const cohortList = level.cohort_list ?? [];
  const excluded = excludedNote(level);
  return (
    <>
      <Headline level={level} definitions={definitions} />
      <div>
        <OutcomesCard
          funnel={level.funnel}
          definitions={definitions}
          inProgress={(level.cohorts?.ended ?? 0) === 0}
        />
        {excluded && <p className="mt-2 text-xs text-gray-500">{excluded}</p>}
      </div>
      <Card title={`Cohorts that started in ${level.year}`}>
        {cohortList.length === 0 ? (
          <p className="text-sm text-gray-500">No cohorts in this year.</p>
        ) : (
          <LevelTable
            kind="cohort"
            rows={cohortList}
            withoutProgress={withoutProgress}
            onSelect={onSelectCohort}
          />
        )}
        <Note>
          Year totals count each learner once, so they can be lower than the sum
          of these cohorts. Select a cohort to see its detail.
        </Note>
      </Card>
      <LearnersCard
        title={`Who our learners were in ${level.year}`}
        breakdowns={level.breakdowns}
        scopeNote={`cohorts that started in ${level.year}`}
      />
      <ProjectsCard
        projects={level.projects}
        action={<ProjectsDownload scope={{ year }} />}
      />
    </>
  );
}

function CohortView({ cohortId, onBack, onLoaded, definitions }) {
  const { data, isLoading, isError, errorStatus, refetch } = useImpactReport(
    `/impact/cohorts/${cohortId}`
  );

  React.useEffect(() => {
    if (data?.year && onLoaded) onLoaded(data.year, data.cohort?.name);
  }, [data, onLoaded]);

  if (isLoading) return <LoadingBlock />;
  if (isError || !data?.cohort) {
    return (
      <ErrorBlock
        status={errorStatus}
        onRetry={refetch}
        onBack={onBack}
        notFoundText="This cohort isn't part of the impact report."
      />
    );
  }

  const { cohort } = data;
  return (
    <>
      <div className={`${CARD} px-5 py-4 flex flex-wrap items-center gap-3`}>
        <h2 className="text-base font-semibold text-gray-900">{cohort.name}</h2>
        <StatusPill status={cohort.status} />
        <span className="text-xs text-gray-500">
          {formatDate(cohort.start_date)} – {formatDate(cohort.end_date)}
        </span>
      </div>
      <Headline level={cohort} definitions={definitions} isCohort />
      <OutcomesCard
        funnel={cohort.funnel}
        definitions={definitions}
        inProgress={cohort.status !== 'ended'}
      />
      <LearnersCard
        title="Who the learners were"
        breakdowns={cohort.breakdowns}
        scopeNote="this cohort"
      />
      <ProjectsCard
        projects={cohort.projects}
        action={<ProjectsDownload scope={{ cohortId }} />}
      />
    </>
  );
}

function Footnotes({ summary }) {
  const notes = summary.notes ?? {};
  const definitions = Object.entries(summary.definitions ?? {});
  const dataNotes = notes.data_notes ?? [];
  const withoutProgress = notes.cohorts_without_progress_data ?? [];
  const threshold = summary.suppression_threshold ?? 5;

  return (
    <section className="space-y-4 pb-8">
      {definitions.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-2">
            Definitions
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2 text-xs text-gray-600">
            {definitions.map(([term, meaning]) => (
              <div key={term}>
                <dt className="inline font-semibold text-gray-800 capitalize">
                  {term.replace(/_/g, ' ')}:{' '}
                </dt>
                <dd className="inline">{meaning}</dd>
              </div>
            ))}
            <div>
              <dt className="inline font-semibold text-gray-800">
                Suppression:{' '}
              </dt>
              <dd className="inline" title={SUPPRESSED_HINT}>
                groups of 1 to {threshold - 1} show as &quot;&lt; {threshold}
                &quot;.
              </dd>
            </div>
          </dl>
        </div>
      )}
      {(dataNotes.length > 0 || withoutProgress.length > 0) && (
        <ul className="list-disc pl-4 space-y-1 text-xs text-gray-500">
          {dataNotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
          {withoutProgress.length > 0 && (
            <li>
              No progress data (so no Active figure) for:{' '}
              {withoutProgress.join(', ')}.
            </li>
          )}
        </ul>
      )}
      <p className="text-xs text-gray-500">
        Aggregated data only, with small groups suppressed. No individual
        learner records.
      </p>
    </section>
  );
}

function YearPicker({ years, selectedYear, onAll, onYear }) {
  const options = [{ key: '', label: 'All years' }].concat(
    years.map((year) => ({ key: String(year.year), label: String(year.year) }))
  );
  return (
    <div
      className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-1"
      role="tablist"
      aria-label="Year"
    >
      {options.map((option) => {
        const active = (selectedYear ?? '') === option.key;
        return (
          <button
            key={option.key || 'all'}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => (option.key ? onYear(option.key) : onAll())}
            className={`px-3 py-1.5 rounded-md text-sm tabular-nums transition-colors ${
              active
                ? 'bg-gray-900 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ImpactReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedYear = searchParams.get('year');
  const selectedCohort = searchParams.get('cohort');
  const [cohortName, setCohortName] = React.useState('');
  const [pdfOpen, setPdfOpen] = React.useState(false);

  React.useEffect(() => {
    setCohortName('');
  }, [selectedCohort]);

  const {
    data: summary,
    isLoading,
    isError,
    errorStatus,
    refetch,
  } = useImpactReport('/impact/summary');

  const go = (params) => {
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const showAllYears = () => go({});
  const selectYear = (year) => go({ year: String(year) });
  const selectCohort = (cohortId) =>
    go({ ...(selectedYear ? { year: selectedYear } : {}), cohort: cohortId });

  // A shared cohort link may arrive without its year; fill it in once known.
  const handleCohortLoaded = React.useCallback(
    (year, name) => {
      setCohortName(name || '');
      if (!selectedYear && year) {
        setSearchParams(
          { year: String(year), cohort: selectedCohort },
          { replace: true }
        );
      }
    },
    [selectedYear, selectedCohort, setSearchParams]
  );

  const years = summary?.years ?? [];
  const firstYear = years[0]?.year;
  const definitions = summary?.definitions ?? {};
  const withoutProgress = React.useMemo(
    () => noProgressNames(summary),
    [summary]
  );
  const scope = selectedCohort
    ? { cohortId: selectedCohort }
    : { year: selectedYear };

  // The level shown, for the PDF. These share the views' query cache, so
  // opening the PDF dialog doesn't fetch anything new.
  const { data: yearData } = useImpactReport(
    `/impact/years/${selectedYear}`,
    Boolean(selectedYear) && !selectedCohort
  );
  const { data: cohortData } = useImpactReport(
    `/impact/cohorts/${selectedCohort}`,
    Boolean(selectedCohort)
  );
  let pdfKind = 'all';
  let pdfLevel = summary?.all_time;
  if (selectedCohort) {
    pdfKind = 'cohort';
    pdfLevel = cohortData?.cohort;
  } else if (selectedYear) {
    pdfKind = 'year';
    pdfLevel = yearData?.year;
  }
  const lastYear = years[years.length - 1]?.year;
  const pdfTitle = {
    all: 'Impact Report',
    year: `Impact Report ${selectedYear}`,
    cohort: pdfLevel?.name ?? 'Cohort report',
  }[pdfKind];
  const pdfPeriod = {
    all: firstYear ? `All cohorts, ${firstYear}–${lastYear}` : 'All cohorts',
    year: `Cohorts that started in ${selectedYear}`,
    cohort: pdfLevel
      ? `${formatDate(pdfLevel.start_date)} – ${formatDate(pdfLevel.end_date)}`
      : '',
  }[pdfKind];

  const buildPdf = async (narrative, brand) => {
    // Loaded on demand so jsPDF stays out of the main bundle.
    const { default: buildImpactPdf } = await import(
      '../components/impact/pdf/buildImpactPdf'
    );
    const doc = await buildImpactPdf({
      kind: pdfKind,
      level: pdfLevel,
      summary,
      title: pdfTitle,
      period: pdfPeriod,
      narrative,
      partner: brand?.partner,
      partnerFirst: brand?.partnerFirst,
    });
    const slug = { all: 'all-years', year: selectedYear, cohort: 'cohort' }[
      pdfKind
    ];
    return {
      doc,
      filename: `plp-impact-report-${
        brand?.partner
          ? `${brand.partner.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '')}-`
          : ''
      }${slug}-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
    };
  };
  const subtitle = [
    summary?.all_time?.cohorts?.total !== undefined && firstYear
      ? `${summary.all_time.cohorts.total} cohorts since ${firstYear}`
      : null,
    summary?.generated_at
      ? `Updated ${format(parseISO(summary.generated_at), 'd MMM yyyy, HH:mm')}`
      : null,
    (summary?.notes?.excluded_test_cohorts ?? []).length
      ? 'Test cohorts excluded'
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const renderBody = () => {
    if (isLoading) return <LoadingBlock />;
    if (isError || !summary) {
      return <ErrorBlock status={errorStatus} onRetry={refetch} />;
    }
    if (selectedCohort) {
      return (
        <CohortView
          cohortId={selectedCohort}
          onBack={showAllYears}
          onLoaded={handleCohortLoaded}
          definitions={definitions}
        />
      );
    }
    if (selectedYear) {
      return (
        <YearView
          year={selectedYear}
          onSelectCohort={selectCohort}
          onBack={showAllYears}
          definitions={definitions}
          withoutProgress={withoutProgress}
        />
      );
    }
    return (
      <AllYearsView
        summary={summary}
        onSelectYear={selectYear}
        withoutProgress={withoutProgress}
      />
    );
  };

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-claret-500">
              Power Learn Project · Impact
            </p>
            <h1 className="text-2xl font-semibold text-gray-900">
              Impact Report
            </h1>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          {summary && (
            <div className="flex flex-wrap items-center gap-2">
              <YearPicker
                years={years}
                selectedYear={selectedYear}
                onAll={showAllYears}
                onYear={selectYear}
              />
              <Link
                to="/impact/partners"
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:border-gray-400"
              >
                Partners
              </Link>
              <DownloadMenu
                options={downloadOptions(scope, () => setPdfOpen(true))}
              />
            </div>
          )}
        </header>

        {(selectedYear || selectedCohort) && summary && (
          <nav
            aria-label="Report level"
            className="flex flex-wrap items-center gap-1 text-sm"
          >
            <button
              type="button"
              onClick={showAllYears}
              className="text-gray-500 hover:text-claret-500"
            >
              All years
            </button>
            {selectedYear && (
              <>
                <ChevronRightIcon fontSize="small" className="text-gray-400" />
                {selectedCohort ? (
                  <button
                    type="button"
                    onClick={() => selectYear(selectedYear)}
                    className="text-gray-500 hover:text-claret-500"
                  >
                    {selectedYear}
                  </button>
                ) : (
                  <span className="text-gray-900">{selectedYear}</span>
                )}
              </>
            )}
            {selectedCohort && (
              <>
                <ChevronRightIcon fontSize="small" className="text-gray-400" />
                <span className="text-gray-900">{cohortName || 'Cohort'}</span>
              </>
            )}
          </nav>
        )}

        {renderBody()}

        {summary && <Footnotes summary={summary} />}
      </div>
      {summary && pdfLevel && (
        <ImpactPdfDialog
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          scope={scope}
          scopeLabel={
            pdfKind === 'all'
              ? 'all years'
              : pdfTitle.replace('Impact Report ', '')
          }
          level={pdfLevel}
          summary={summary}
          issues={dataQualityIssues(
            pdfLevel,
            pdfKind === 'all' ? summary.notes : null
          )}
          onBuild={buildPdf}
          reportTitle={pdfTitle}
          period={pdfPeriod}
        />
      )}
    </MainContent>
  );
}

export default ImpactReport;
